import { type ReactNode, type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

export type CardVariant =
  | 'glass'
  | 'glass-strong'
  | 'kinetic'
  | 'kinetic-sm'
  | 'glow'
  | 'cyan'
  | 'lime'
  | 'critical'
  // legacy aliases (kept for backward-compat with pages mid-reskin)
  | 'neu'
  | 'neu-sm'
  | 'neu-lg'
  | 'neu-pressed';

export interface GlassCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  rounded?: 'none' | 'sm' | 'md' | 'lg' | '2xl' | '3xl' | 'default' | 'liquid';
  interactive?: boolean;
}

const PADDING_MAP = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const ROUNDED_MAP = {
  none: '',
  sm: 'rounded-sm',
  md: 'rounded',
  lg: 'rounded-lg',
  '2xl': 'rounded-2xl',
  '3xl': 'rounded-3xl',
  default: 'rounded-2xl',
  liquid: 'rounded-2xl',
};

const VARIANT_CLASS: Record<CardVariant, string> = {
  glass: 'bg-surface-container border-2 border-outline',
  'glass-strong': 'bg-surface-container border-2 border-outline shadow-[4px_4px_0_0_#000000]',
  kinetic: 'bg-surface-container border-2 border-outline shadow-[4px_4px_0_0_#000000]',
  'kinetic-sm': 'bg-surface-container border-2 border-outline shadow-[2px_2px_0_0_#000000]',
  glow: 'bg-surface-container border-2 border-primary shadow-[0_0_32px_0_rgba(255,138,101,0.25),4px_4px_0_0_#000000]',
  cyan: 'bg-surface-container border-2 border-secondary shadow-[0_0_24px_0_rgba(34,211,238,0.20),4px_4px_0_0_#000000]',
  lime: 'bg-surface-container border-2 border-tertiary shadow-[0_0_24px_0_rgba(163,230,53,0.20),4px_4px_0_0_#000000]',
  critical: 'bg-surface-container border-2 border-error shadow-[0_0_24px_0_rgba(239,68,68,0.25),4px_4px_0_0_#000000]',
  // legacy aliases (render as kinetic variant)
  neu: 'bg-surface-container border-2 border-outline shadow-[4px_4px_0_0_#000000]',
  'neu-sm': 'bg-surface-container border-2 border-outline shadow-[2px_2px_0_0_#000000]',
  'neu-lg': 'bg-surface-container border-2 border-outline shadow-[6px_6px_0_0_#000000]',
  'neu-pressed': 'bg-surface-container border-2 border-primary',
};

export const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(function GlassCard(
  { children, className, variant = 'kinetic', padding = 'md', rounded = 'none', interactive, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        VARIANT_CLASS[variant],
        PADDING_MAP[padding],
        ROUNDED_MAP[rounded],
        interactive &&
          'transition-transform duration-150 cursor-pointer hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[6px_6px_0_0_#ff8a65]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
